/**
 * OperationalManagerSettings.jsx — CRUD de Operational Managers no painel de admin.
 */

import React, { useEffect, useState } from "react";
import {
  fetchOperationalManagers,
  createOperationalManager,
  updateOperationalManager,
  deleteOperationalManager,
  resolverIdsOMs,
} from "../../services/api";

export default function OperationalManagerSettings() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nome: "", email: "", ms365_user_id: "", tipo: "om" });
  const [modoForm, setModoForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [resolvendo, setResolvendo] = useState(false);
  const [resultadoResolver, setResultadoResolver] = useState(null);

  const carregar = () => {
    setLoading(true);
    fetchOperationalManagers()
      .then(setLista)
      .catch((e) => setErro(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, []);

  const oms   = lista.filter((om) => (om.tipo || "om") !== "apoio");
  const apoio = lista.filter((om) => om.tipo === "apoio");

  const iniciarNovo = () => {
    setEditando(null);
    setForm({ nome: "", email: "", ms365_user_id: "", tipo: "om" });
    setModoForm(true);
  };

  const iniciarEdicao = (om) => {
    setEditando(om);
    setForm({ nome: om.nome, email: om.email || "", ms365_user_id: om.ms365_user_id || "", tipo: om.tipo || "om" });
    setModoForm(false);
  };

  const cancelar = () => { setEditando(null); setModoForm(false); setErro(null); };

  const guardar = async () => {
    if (!form.nome.trim()) { setErro("O nome é obrigatório."); return; }
    setSalvando(true);
    setErro(null);
    try {
      const payload = {
        nome: form.nome.trim(),
        email: form.email.trim() || null,
        ms365_user_id: form.ms365_user_id.trim() || null,
        tipo: form.tipo,
      };
      if (modoForm) {
        await createOperationalManager(payload);
      } else {
        await updateOperationalManager(editando.id, payload);
      }
      cancelar();
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (om) => {
    if (!window.confirm(`Remover "${om.nome}"?`)) return;
    await deleteOperationalManager(om.id);
    carregar();
  };

  const resolverIds = async () => {
    setResolvendo(true);
    setResultadoResolver(null);
    setErro(null);
    try {
      const res = await resolverIdsOMs();
      setResultadoResolver(res);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setResolvendo(false);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.titulo}>Operational Managers</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={{ ...styles.btnNovo, background: "#0ea5e9" }}
            onClick={resolverIds}
            disabled={resolvendo}
            title="Resolve automaticamente o MS365 ID para OMs que só têm email"
          >
            {resolvendo ? "A resolver…" : "Resolver IDs via M365"}
          </button>
          <button style={styles.btnNovo} onClick={iniciarNovo}>+ Novo OM</button>
        </div>
      </div>

      {resultadoResolver && (
        <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 6, padding: "10px 16px", fontSize: 13, color: "#166534", marginBottom: 16 }}>
          ✅ {resultadoResolver.resolvidos} de {resultadoResolver.total} OMs resolvidos com sucesso.
        </div>
      )}

      {erro && <div style={styles.erro}>{erro}</div>}

      {(modoForm || editando) && (
        <div style={styles.formCard}>
          <h3 style={styles.formTitulo}>
            {modoForm ? "Novo Operational Manager" : `Editar: ${editando.nome}`}
          </h3>
          <div style={styles.formGrid}>
            <div style={styles.formGrupo}>
              <label style={styles.label}>Nome *</label>
              <input style={styles.input} value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Nome completo" />
            </div>
            <div style={styles.formGrupo}>
              <label style={styles.label}>Email</label>
              <input style={styles.input} value={form.email} type="email"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@exemplo.com" />
            </div>
            <div style={styles.formGrupo}>
              <label style={styles.label}>Tipo</label>
              <select style={styles.input} value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                <option value="om">Operational Manager</option>
                <option value="apoio">Apoio (designer, fotógrafo, ...)</option>
              </select>
            </div>
            <div style={{ ...styles.formGrupo, gridColumn: "1 / -1" }}>
              <label style={styles.label}>Microsoft 365 User ID</label>
              <input style={styles.input} value={form.ms365_user_id}
                onChange={(e) => setForm({ ...form, ms365_user_id: e.target.value })}
                placeholder="GUID do utilizador Microsoft 365 (opcional)" />
              <span style={styles.hint}>
                Preenchido automaticamente a partir do email. Podes também inserir manualmente o Object ID do Azure AD.
              </span>
            </div>
          </div>
          <div style={styles.formActions}>
            <button style={styles.btnGuardar} onClick={guardar} disabled={salvando}>
              {salvando ? "A guardar…" : "Guardar"}
            </button>
            <button style={styles.btnCancelar} onClick={cancelar}>Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={styles.loading}>A carregar…</p>
      ) : lista.length === 0 ? (
        <p style={styles.semDados}>Nenhum Operational Manager criado ainda.</p>
      ) : (
        <table style={styles.tabela}>
          <thead>
            <tr>
              <th style={styles.th}>Nome</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>MS365 ID</th>
              <th style={{ ...styles.th, width: 140 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {oms.map((om) => (
              <OMRow key={om.id} om={om} editando={editando} iniciarEdicao={iniciarEdicao} remover={remover} />
            ))}
            {apoio.length > 0 && (
              <tr>
                <td colSpan={4} style={styles.grupoApoio}>Apoio</td>
              </tr>
            )}
            {apoio.map((om) => (
              <OMRow key={om.id} om={om} editando={editando} iniciarEdicao={iniciarEdicao} remover={remover} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function OMRow({ om, editando, iniciarEdicao, remover }) {
  return (
    <tr style={editando?.id === om.id ? styles.trEditando : styles.tr}>
      <td style={styles.td}>
        {om.nome}
        {om.tipo === "apoio" && <span style={styles.chipApoio}>Apoio</span>}
      </td>
      <td style={styles.td}>{om.email || "—"}</td>
      <td style={{ ...styles.td, fontFamily: "monospace", fontSize: 11, color: "#94a3b8" }}>
        {om.ms365_user_id
          ? `${om.ms365_user_id.substring(0, 8)}…`
          : <span style={{ color: "#cbd5e1" }}>não ligado</span>
        }
      </td>
      <td style={styles.td}>
        <div style={styles.acoes}>
          <button style={styles.btnEditar} onClick={() => iniciarEdicao(om)}>Editar</button>
          <button style={styles.btnRemover} onClick={() => remover(om)}>Remover</button>
        </div>
      </td>
    </tr>
  );
}

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  titulo: { fontSize: 16, fontWeight: 600, margin: 0, color: "#1e293b" },
  btnNovo: { padding: "8px 16px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  formCard: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20, marginBottom: 20 },
  formTitulo: { fontSize: 14, fontWeight: 600, margin: "0 0 16px", color: "#374151" },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 },
  formGrupo: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: "#475569" },
  input: { padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13 },
  hint: { fontSize: 11, color: "#94a3b8", marginTop: 4 },
  formActions: { display: "flex", gap: 8 },
  btnGuardar: { padding: "8px 20px", background: "#22c55e", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnCancelar: { padding: "8px 16px", background: "none", color: "#64748b", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, cursor: "pointer" },
  tabela: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 12px", backgroundColor: "#f8fafc", fontWeight: 600, borderBottom: "2px solid #e2e8f0", color: "#475569" },
  tr: { borderBottom: "1px solid #f1f5f9" },
  trEditando: { borderBottom: "1px solid #f1f5f9", backgroundColor: "#fefce8" },
  td: { padding: "10px 12px", color: "#374151" },
  acoes: { display: "flex", gap: 6 },
  btnEditar: { padding: "4px 12px", background: "#e0e7ff", color: "#4338ca", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnRemover: { padding: "4px 12px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  semDados: { color: "#94a3b8", fontSize: 14 },
  loading: { color: "#64748b", fontSize: 14 },
  erro: { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, color: "#dc2626", marginBottom: 16, fontSize: 13 },
  grupoApoio: { padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#9333ea", textTransform: "uppercase", letterSpacing: 0.5, background: "#faf5ff", borderTop: "1px solid #e9d5ff", borderBottom: "1px solid #e9d5ff" },
  chipApoio: { marginLeft: 8, padding: "2px 8px", borderRadius: 999, background: "#f3e8ff", color: "#9333ea", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 },
};
